import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { InferType } from 'yup';

import { FunctionComponent } from '@sbv-clients/util-misc';

import schema from '../schema';
import { useResetPassword } from '../utils/use-reset-password';

type FormValues = InferType<typeof schema>;

interface ResetPasswordResultContextValue {
  methods: UseFormReturn<FormValues>;
  handleSubmit: SubmitHandler<FormValues>;
}

const ResetPasswordResultContext = createContext<
  ResetPasswordResultContextValue | undefined
>(undefined);

export function useResetPasswordResult(): ResetPasswordResultContextValue {
  return useContext(ResetPasswordResultContext)!;
}

export const ResetPasswordProvider: FunctionComponent = ({ children }) => {
  const resetPassword = useResetPassword();

  const methods = useForm<FormValues>({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const handleSubmit = useCallback(
    async ({ password, otp, username }: FormValues) => {
      try {
        await resetPassword.mutateAsync({
          passwordResetRequest: { newPassword: password, otp },
          username,
        });
      } catch {
        /* empty */
      }
    },
    [resetPassword]
  );

  const context: ResetPasswordResultContextValue = useMemo(
    () => ({
      methods,
      handleSubmit,
    }),
    [handleSubmit, methods]
  );
  return (
    <ResetPasswordResultContext.Provider value={context}>
      {children}
    </ResetPasswordResultContext.Provider>
  );
};
